import React, { createContext, useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { useMutation, useQuery, gql } from '@apollo/client';
import { setCookie, getCookie, removeCookie } from '../utils/cookies';

// GraphQL Queries and Mutations
const GET_USER_QUERY = gql`
  query GetUser($account: String!) {
    getUser(account: $account) {
      id
      account
      name
      email
      nftOwned
      isCreator
    }
  }
`;

const CREATE_USER_MUTATION = gql`
  mutation CreateUser($account: String!, $name: String!, $email: String!) {
    createUser(account: $account, name: $name, email: $email) {
      id
      account
      name
      email
    }
  }
`;

const AUTHENTICATE_USER_MUTATION = gql`
  mutation AuthenticateUser($address: String!, $signature: String!, $message: String!) {
    authenticateUser(address: $address, signature: $signature, message: $message)
  }
`;

// Create Web3Context
export const Web3Context = createContext();

export const Web3Provider = ({ children }) => {
  const [account, setAccount] = useState(null);
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [userInfo, setUserInfo] = useState({ name: '', email: '' });
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [userAuth, setUserAuth] = useState(false);
  const [walletConnected, setWalletConnected] = useState(false);

  const { data: userData, refetch, loading: userLoading, error: userError } = useQuery(GET_USER_QUERY, {
    variables: { account: account || '' },
    skip: !account,
    onCompleted: (data) => {
      if (data && data.getUser) {
        setUser(data.getUser);
        setUserInfo({ name: data.getUser.name, email: data.getUser.email });
      } else {
        setUser(null);
        setUserInfo({ name: '', email: '' });
      }
    },
    onError: (err) => {
      console.error('Error fetching user data:', err);
      setUser(null);
      setUserInfo({ name: '', email: '' });
    }
  });

  const [createUser] = useMutation(CREATE_USER_MUTATION, {
    onCompleted: (data) => {
      if (data && data.createUser) {
        setUser(data.createUser);
        setUserInfo({ name: data.createUser.name, email: data.createUser.email });
      }
    },
    onError: (err) => {
      console.error('Error creating user:', err);
      setError('Error creating user: ' + err.message);
    }
  });

  const [authenticateUser] = useMutation(AUTHENTICATE_USER_MUTATION, {
    onCompleted: (data) => {
      if (data && data.authenticateUser) {
        setCookie('token', data.authenticateUser, 1);
        setUserAuth(true);
      }
    },
    onError: (err) => {
      console.error('Error authenticating user:', err);
      setError('Authentication failed: ' + err.message);
      removeCookie('token');
    }
  });

  // Function to initialize provider and fetch account
  const initializeProvider = useCallback(async () => {
    if (window.ethereum) {
      const _provider = new ethers.BrowserProvider(window.ethereum);
      setProvider(_provider);

      try {
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
        if (accounts.length > 0) {
          setAccount(accounts[0]);
          const _signer = _provider.getSigner();
          setSigner(_signer);
          setWalletConnected(true);
        }
      } catch (error) {
        if (error.code === 4001) {
          setError("User denied account access");
        } else {
          setError("Error connecting to wallet");
        }
      }

      // Add event listeners
      window.ethereum.on('accountsChanged', (accounts) => {
        if (accounts.length === 0) {
          setAccount(null);
          setUser(null);
          setUserInfo({ name: '', email: '' });
          removeCookie('token');
        } else {
          setAccount(accounts[0]);
          refetch();
        }
      });

      window.ethereum.on('chainChanged', () => {
        window.location.reload();
      });
    } else {
      setError("No Ethereum provider found. Install MetaMask or Coinbase Wallet.");
    }
  }, [refetch]);

  // Effect to initialize provider on component mount
  useEffect(() => {
    if (userAuth) {
      initializeProvider();
      refetch();
    }
  }, [userAuth, refetch, initializeProvider]);

  // Effect to refetch user data when account changes
  useEffect(() => {
    if (account) {
      refetch();
    }
  }, [account, refetch]);

  const registerUser = async () => {
    try {
      if (account && !user) {
        await createUser({
          variables: {
            account,
            name: userInfo.name,
            email: userInfo.email,
          },
        });
        refetch();
      }
    } catch (error) {
      setError('Error creating user: ' + error.message);
    }
  };

  return (
    <Web3Context.Provider value={{ account, provider, signer, userInfo, setUserInfo, user, registerUser, error, setUserAuth, setAccount, walletConnected }}>
      {children}
    </Web3Context.Provider>
  );
};
